import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Network, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

// iter317 — Divisions belong to a Trade (division.trade_id). Each Trade's list is independent.
const DivRow = ({ d, idx, total, onSave, onMove, usage }) => {
  const [name, setName] = useState(d.name);
  useEffect(() => setName(d.name), [d.name]);
  return (
    <div data-testid={`division-row-${d.id}`} className={`grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1fr_140px_110px_170px_auto] gap-3 items-center px-3 py-3 border-t border-[#262626] ${d.active ? '' : 'opacity-50'}`}>
      <div className="flex flex-col">
        <button data-testid={`division-up-${d.id}`} disabled={idx === 0} onClick={() => onMove(idx, -1)} className="text-[#A3A3A3] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
        <button data-testid={`division-down-${d.id}`} disabled={idx === total - 1} onClick={() => onMove(idx, 1)} className="text-[#A3A3A3] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
      </div>
      <Input data-testid={`division-name-${d.id}`} value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== d.name && onSave(d, { name: name.trim() }, 'Renamed')} className="bg-[#0A0A0A] border-[#262626] text-white h-9" />
      <span data-testid={`division-trade-${d.id}`} className="hidden md:block text-xs text-[#D4AF37]">{d.trade_name}</span>
      <span className={`hidden md:block text-[10px] font-bold uppercase ${d.active ? 'text-emerald-400' : 'text-[#737373]'}`} data-testid={`division-status-${d.id}`}>
        {d.active ? 'Active' : 'Inactive'}{usage ? <span className="block text-[#737373] font-normal normal-case">{usage.appointments} visits · {usage.employees} staff · {usage.service_types} types</span> : null}
      </span>
      <label className="flex col-span-3 md:col-span-1 pl-7 md:pl-0 items-center gap-2 text-[11px] text-[#A3A3A3]" title="Unassigned jobs in THIS Trade's division can be claimed by eligible employees of this Trade from any division">
        <input data-testid={`division-pool-${d.id}`} type="checkbox" checked={d.shared_claim_pool} onChange={(e) => onSave(d, { shared_claim_pool: e.target.checked }, 'Updated')} /> Shared claim pool
      </label>
      <Button data-testid={`division-toggle-${d.id}`} size="sm" variant="outline" onClick={() => onSave(d, { active: !d.active }, d.active ? 'Deactivated' : 'Reactivated')} className={d.active ? 'border-[#7F1D1D] text-red-400' : 'border-green-700 text-green-400'}>{d.active ? 'Deactivate' : 'Reactivate'}</Button>
    </div>
  );
};

export default function AdminDivisions() {
  const [rows, setRows] = useState([]);
  const [trades, setTrades] = useState([]);
  const [trade, setTrade] = useState('');
  const [usage, setUsage] = useState({});
  const [newName, setNewName] = useState('');
  const load = useCallback(async () => {
    try {
      const [d, t, u] = await Promise.all([
        axios.get(`${API}/divisions?include_inactive=true`, opts),
        axios.get(`${API}/inv/departments`, opts),
        axios.get(`${API}/admin/divisions/usage`, opts).catch(() => ({ data: { divisions: [] } })),
      ]);
      setRows(d.data);
      const active = (t.data || []).filter((x) => x.active !== false);
      setTrades(active);
      setTrade((cur) => cur || active[0]?.id || '');
      setUsage(Object.fromEntries((u.data.divisions || []).map((x) => [x.id, x])));
    } catch { toast.error('Failed to load divisions'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => rows.filter((d) => d.trade_id === trade), [rows, trade]);
  const tradeName = trades.find((t) => t.id === trade)?.name || '';
  const save = async (d, patch, msg) => {
    try { await axios.put(`${API}/admin/divisions/${d.id}`, patch, opts); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const create = async () => {
    if (!newName.trim()) return toast.error('Name required');
    try { await axios.post(`${API}/admin/divisions`, { name: newName.trim(), trade_id: trade }, opts); toast.success(`Division added to ${tradeName}`); setNewName(''); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    return null;
  };
  const move = async (idx, dir) => {
    const next = [...shown]; const j = idx + dir;
    [next[idx], next[j]] = [next[j], next[idx]];
    await Promise.all(next.map((d, i) => (d.sort_order !== i ? axios.put(`${API}/admin/divisions/${d.id}`, { sort_order: i }, opts) : null)));
    load();
  };
  return (
    <div className="space-y-6" data-testid="divisions-page">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Network className="w-7 h-7 text-[#D4AF37]" /> Divisions</h2>
        <p className="text-[#A3A3A3] text-sm max-w-3xl">Each Trade has its own Divisions (Trade → Division → Service Type). Two Trades can both have a “Service” division — they are separate schedules, scopes and claim pools. Renaming a label never rewrites history.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="division-trade-tabs">
        {trades.map((t) => (
          <button key={t.id} data-testid={`division-trade-tab-${t.id}`} onClick={() => { setTrade(t.id); setNewName(''); }}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${trade === t.id ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold' : 'border-[#262626] text-[#A3A3A3] hover:border-[#D4AF37]/60'}`}>
            {t.name}{t.type === 'internal' ? ' (internal)' : ''}
            <span className="ml-1.5 text-[10px] opacity-70">{rows.filter((d) => d.trade_id === t.id).length}</span>
          </button>
        ))}
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-2">
        <p className="text-white font-semibold text-sm">Add a Division to <span className="text-[#D4AF37]" data-testid="division-new-trade">{tradeName || '—'}</span></p>
        <div className="flex gap-2">
          <Input data-testid="division-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} placeholder="e.g. Install, Maintenance" className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Button data-testid="division-create" disabled={!trade} onClick={create} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Division</Button>
        </div>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden" data-testid="division-list">
        <div className="hidden md:grid grid-cols-[auto_1fr_140px_110px_170px_auto] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[#737373]">
          <span className="w-4" /><span>Division</span><span>Trade</span><span>Status</span><span>Claim pool</span><span />
        </div>
        {shown.length === 0 && <p className="px-3 py-6 text-sm text-[#737373]" data-testid="division-empty">No divisions for {tradeName} yet — add one above.</p>}
        {shown.map((d, i) => <DivRow key={d.id} d={d} idx={i} total={shown.length} onSave={save} onMove={move} usage={usage[d.id]} />)}
      </div>
    </div>
  );
}
